import clsx from "clsx";
import { FC, MouseEvent } from "react";

import { Icon } from "@tsmono/react/icons";

import styles from "./BranchPoint.module.css";
import type { ForkNavData } from "./timeline/timelineEventNodes";

export interface BranchPointProps {
  /** Branch that already existed and continues past this fork. */
  parent: string;
  /** Branches born at this fork, in display order. */
  spawned: string[];
  /** The branch the viewer is currently reading. Must equal `parent` or one of `spawned`. */
  viewing: string;
  /**
   * Called when a non-current segment is clicked. The second arg is the
   * clicked element so the host can scroll-anchor (preserves viewport
   * position after the selection change re-renders the transcript list).
   */
  onSelect?: (branch: string, anchorEl: HTMLElement) => void;
  className?: string;
}

export const BranchPoint: FC<BranchPointProps> = ({
  parent,
  spawned,
  viewing,
  onSelect,
  className,
}) => {
  if (spawned.length === 0) return null;

  const options = [parent, ...spawned];

  return (
    <div
      role="group"
      aria-label="Branch point"
      className={clsx(styles.branchPoint, className)}
    >
      <span className={styles.label}>
        <Icon name="fork" className={styles.glyph} />
        <span className={styles.labelText}>Branch point</span>
      </span>
      <div
        role="radiogroup"
        aria-label="Choose branch"
        className={styles.segmented}
      >
        {options.map((b) => (
          <Segment
            key={b}
            branch={b}
            isCurrent={b === viewing}
            isParent={b === parent}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
};

interface SegmentProps {
  branch: string;
  isCurrent: boolean;
  isParent: boolean;
  onSelect?: (branch: string, anchorEl: HTMLElement) => void;
}

const Segment: FC<SegmentProps> = ({
  branch,
  isCurrent,
  isParent,
  onSelect,
}) => {
  const handleClick =
    onSelect && !isCurrent
      ? (e: MouseEvent<HTMLButtonElement>): void =>
          onSelect(branch, e.currentTarget)
      : undefined;
  return (
    <button
      type="button"
      role="radio"
      className={styles.segment}
      data-testid="bp-segment"
      data-branch={branch}
      aria-checked={isCurrent}
      onClick={handleClick}
      disabled={!handleClick}
    >
      {isParent && <ContinuesGlyph />}
      <span>{branch}</span>
    </button>
  );
};

const ContinuesGlyph: FC = () => (
  <svg
    className={styles.continuesGlyph}
    viewBox="0 0 9 9"
    aria-hidden="true"
    data-testid="bp-continues"
  >
    <path d="M 1 1 L 8 4.5 L 1 8 Z" />
  </svg>
);

/**
 * Adapt a merged `fork_nav` payload to BranchPoint props.
 */
export function forkNavToBranchPointProps(
  data: ForkNavData
): Pick<BranchPointProps, "parent" | "spawned" | "viewing"> | null {
  const first = data.groups[0];
  if (!first || first.options.length === 0) return null;
  const parent = first.options[0]!.label;

  // Well-formed data has at most one group with selectedIndex > 0 (the cut
  // anchor's group). If more than one group somehow reports a selection,
  // the last one wins.
  const spawned: string[] = [];
  let viewing = parent;
  for (const group of data.groups) {
    // options[0] is the stay-on-segment pseudo-option (the parent) — skip it.
    for (let i = 1; i < group.options.length; i++) {
      spawned.push(group.options[i]!.label);
    }
    if (group.selectedIndex > 0) {
      viewing = group.options[group.selectedIndex]!.label;
    }
  }
  if (spawned.length === 0) return null;
  return { parent, spawned, viewing };
}

/**
 * Find the swimlane row key for a clicked branch label. First match wins
 * when labels collide across merged groups.
 */
export function findRowKeyForLabel(
  data: ForkNavData,
  label: string
): string | null {
  for (const group of data.groups) {
    for (const opt of group.options) {
      if (opt.label === label) return opt.rowKey;
    }
  }
  return null;
}
